import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getTransaction } from '../lib/api';
import { truncateAddress, copyToClipboard, formatNumber } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { AddressBadge, TxHashBadge } from '../components/AddressBadge';
import { ArrowLeft, Copy, Check, FileText, ArrowRight, ArrowDown, CheckCircle, XCircle, Clock, Hash, Layers, Fuel, Coins, User, Calendar, Repeat, Flame, Database } from 'lucide-react';
import { toast } from 'sonner';

// Token addresses and info
const TOKEN_INFO = {
  "0x9189d145d1d8e612c4c48bbd1af5a50cbee63d7d": { symbol: "WKROWN", name: "Wrapped Krown", decimals: 18 },
  "0xa3d9cfa4220d6a5ba7b9067d33ebedbd2de6f1cf": { symbol: "USDC", name: "USD Coin", decimals: 6 },
  "0x59973ae98d340ca0577ed92502402e82e987ba2e": { symbol: "USDT", name: "Tether USD", decimals: 6 },
  "0x6f9ec4f83ef69b923fa1dc00189e591728df0ac0": { symbol: "ETH", name: "Ether Token", decimals: 18 },
};

const ROUTER_ADDRESS = "0xe0ec010899d166559e7a22216659c8da16525c6d";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

export default function TransactionPage() {
  const { txHash } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getTransaction(txHash);
        setData(result);
      } catch (err) {
        console.error('Failed to fetch transaction:', err);
        setError('Failed to load transaction information');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [txHash]);

  const handleCopy = async (text, field) => {
    try {
      await copyToClipboard(text);
      setCopiedField(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const CopyButton = ({ text, field }) => (
    <button
      onClick={() => handleCopy(text, field)}
      className="p-1.5 rounded hover:bg-muted transition-colors"
      title="Copy to clipboard"
    >
      {copiedField === field ? (
        <Check className="w-4 h-4 text-emerald-500" />
      ) : (
        <Copy className="w-4 h-4 text-muted-foreground" />
      )}
    </button>
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return (
          <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">
            <CheckCircle className="w-3 h-3 mr-1" />
            Success
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/20 text-red-600 border-red-500/30">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const getAddressLabel = (address) => {
    if (!address) return null;
    const lower = address.toLowerCase();
    if (lower === ROUTER_ADDRESS) return "SwapRouter02";
    if (lower === ZERO_ADDRESS) return "Burn Address";
    const token = TOKEN_INFO[lower];
    if (token) return token.name;
    return null;
  };

  const formatTokenAmount = (amount, decimals = 18) => {
    const num = parseFloat(amount) / Math.pow(10, decimals);
    return formatNumber(num.toFixed(6));
  };

  // Analyze transaction to determine action summary
  const getTransactionAction = () => {
    if (!data) return null;
    
    const transfers = data.transfers || [];
    const swaps = data.dex_swaps || [];
    const value = parseFloat(data.value || '0');
    
    // Calculate total amounts for each token type
    const tokenAmounts = {};
    transfers.forEach(t => {
      const symbol = t.token_symbol || 'Unknown';
      const amount = parseFloat(t.amount_formatted?.replace(/,/g, '') || '0');
      tokenAmounts[symbol] = (tokenAmounts[symbol] || 0) + amount;
    });
    
    if (swaps.length > 0) {
      const swap = swaps[0];
      const isBuy = swap.krown_direction === 'in';
      const otherToken = swap.token0_symbol === 'KROWN' || swap.token0_symbol === 'WKROWN' 
        ? swap.token1_symbol 
        : swap.token0_symbol;
      
      // Get amounts from swaps
      const krownAmount = parseFloat(swap.krown_amount || '0');
      const usdVolume = parseFloat(swap.volume_usd || '0');
      
      return {
        type: isBuy ? 'Swap (Buy KROWN)' : 'Swap (Sell KROWN)',
        description: isBuy 
          ? `Swap ${otherToken} for WKROWN` 
          : `Swap WKROWN for ${otherToken}`,
        highlight: true,
        details: {
          krown: formatNumber(krownAmount.toFixed(2)),
          usd: usdVolume > 0 ? `$${formatNumber(usdVolume.toFixed(2))}` : null,
          otherToken
        }
      };
    }
    
    if (transfers.length > 0) {
      const transfer = transfers[0];
      const tokenInfo = TOKEN_INFO[transfer.token_address?.toLowerCase()] || { symbol: transfer.token_symbol || 'Token' };
      const amount = parseFloat(transfer.amount_formatted?.replace(/,/g, '') || '0');
      
      // Highlight non-KROWN transfers
      const isNonKrown = tokenInfo.symbol !== 'KROWN' && tokenInfo.symbol !== 'WKROWN';
      
      return {
        type: `${tokenInfo.symbol} Transfer`,
        description: `Transfer ${formatNumber(amount.toFixed(2))} ${tokenInfo.symbol}`,
        highlight: isNonKrown,
        details: {
          amount: formatNumber(amount.toFixed(2)),
          symbol: tokenInfo.symbol
        }
      };
    }
    
    if (value > 0) {
      return {
        type: 'Native KROWN Transfer',
        description: `Transfer ${formatNumber((value / 1e18).toFixed(6))} KROWN`,
        highlight: false
      };
    }
    
    return { type: 'Contract Interaction', description: 'Contract call', highlight: false };
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20 px-4">
        <h2 className="text-2xl font-bold mb-2">Transaction Not Found</h2>
        <p className="text-muted-foreground mb-4">{error || 'This transaction could not be found.'}</p>
        <Button onClick={() => navigate(-1)} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const txAction = getTransactionAction();
  const hasTokenTransfers = data.transfers && data.transfers.length > 0;
  const hasSwaps = data.dex_swaps && data.dex_swaps.length > 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in p-4" data-testid="transaction-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-2"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Transaction Header */}
      <div className="border-b pb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Transaction Details</h1>
            <p className="text-sm text-muted-foreground">Krown Chain</p>
          </div>
        </div>
        
        {/* Transaction Action Summary - Enhanced */}
        {txAction && (
          <div className={`rounded-lg p-4 mt-4 ${txAction.highlight ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30' : 'bg-muted/50'}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Repeat className={`w-5 h-5 ${txAction.highlight ? 'text-emerald-500' : 'text-primary'}`} />
                <span className="font-bold text-lg">{txAction.type}</span>
              </div>
              {txAction.details && (
                <div className="flex flex-wrap items-center gap-2">
                  {txAction.details.krown && (
                    <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30 text-sm px-3 py-1">
                      {txAction.details.krown} KROWN
                    </Badge>
                  )}
                  {txAction.details.usd && (
                    <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30 text-sm px-3 py-1">
                      {txAction.details.usd}
                    </Badge>
                  )}
                  {txAction.details.amount && txAction.details.symbol && !txAction.details.krown && (
                    <Badge className={`text-sm px-3 py-1 ${
                      txAction.details.symbol === 'USDT' ? 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30' :
                      txAction.details.symbol === 'ETH' ? 'bg-blue-500/20 text-blue-600 border-blue-500/30' :
                      'bg-violet-500/20 text-violet-600 border-violet-500/30'
                    }`}>
                      {txAction.details.amount} {txAction.details.symbol}
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-2">{txAction.description}</p>
          </div>
        )}
      </div>

      {/* Overview Card */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="p-4 border-b bg-muted/30">
            <h2 className="font-semibold">Overview</h2>
          </div>
          <div className="divide-y">
            {/* Transaction Hash */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Hash className="w-4 h-4" />
                <span className="text-sm font-medium">Transaction Hash:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <code className="font-mono text-sm break-all">{txHash}</code>
                <CopyButton text={txHash} field="hash" />
              </div>
            </div>

            {/* Status */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Status:</span>
              </div>
              <div className="flex-1">
                {getStatusBadge(data.status)}
              </div>
            </div>

            {/* Block - Clickable */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Layers className="w-4 h-4" />
                <span className="text-sm font-medium">Block:</span>
              </div>
              <div className="flex-1">
                <Link 
                  to={`/block/${data.block_number}`}
                  className="font-mono text-primary hover:underline"
                  data-testid="block-link"
                >
                  {data.block_number?.toLocaleString() || '—'}
                </Link>
              </div>
            </div>

            {/* Timestamp */}
            {data.block_time && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
                <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm font-medium">Timestamp:</span>
                </div>
                <div className="flex-1">
                  <span>{new Date(data.block_time).toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* From */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">From:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {data.from_address ? (
                  <>
                    <Link 
                      to={`/address/${data.from_address}`}
                      className="font-mono text-sm text-primary hover:underline break-all"
                    >
                      {data.from_address}
                    </Link>
                    <CopyButton text={data.from_address} field="from" />
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
            </div>

            {/* To */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <ArrowRight className="w-4 h-4" />
                <span className="text-sm font-medium">To:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {data.to_address ? (
                  <>
                    <Link 
                      to={`/address/${data.to_address}`}
                      className="font-mono text-sm text-primary hover:underline break-all"
                    >
                      {data.to_address}
                    </Link>
                    {getAddressLabel(data.to_address) && (
                      <Badge variant="outline" className="text-xs">{getAddressLabel(data.to_address)}</Badge>
                    )}
                    <CopyButton text={data.to_address} field="to" />
                  </>
                ) : (
                  <span className="text-muted-foreground">Contract Creation</span>
                )}
              </div>
            </div>

            {/* Value - Show native KROWN transfer or indicate token transfer */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Coins className="w-4 h-4" />
                <span className="text-sm font-medium">Value:</span>
              </div>
              <div className="flex-1">
                {parseInt(data.value || '0') > 0 ? (
                  <span className="font-mono font-medium text-emerald-600">
                    {formatNumber((parseInt(data.value) / 1e18).toFixed(6))} KROWN
                  </span>
                ) : hasTokenTransfers || hasSwaps ? (
                  <span className="text-muted-foreground italic">
                    0 KROWN (see token transfers below)
                  </span>
                ) : (
                  <span className="text-muted-foreground">0 KROWN</span>
                )}
              </div>
            </div>

            {/* Transaction Fee */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Fuel className="w-4 h-4" />
                <span className="text-sm font-medium">Transaction Fee:</span>
              </div>
              <div className="flex-1 font-mono">
                {data.gas_used && data.gas_price ? (
                  formatNumber((data.gas_used * parseInt(data.gas_price) / 1e18).toFixed(12)) + ' KROWN'
                ) : '—'}
              </div>
            </div>

            {/* Gas Price */}
            {data.gas_price && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
                <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                  <Fuel className="w-4 h-4" />
                  <span className="text-sm font-medium">Gas Price:</span>
                </div>
                <div className="flex-1 font-mono">
                  {(parseInt(data.gas_price) / 1e9).toFixed(9)} Gwei
                </div>
              </div>
            )}

            {/* Gas Limit & Usage */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-4 hover:bg-muted/30">
              <div className="flex items-center gap-2 sm:w-48 text-muted-foreground">
                <Fuel className="w-4 h-4" />
                <span className="text-sm font-medium">Gas Limit & Usage:</span>
              </div>
              <div className="flex-1 font-mono">
                {data.gas_used?.toLocaleString() || '—'} | {data.gas_limit?.toLocaleString() || '—'}
                {data.gas_used && data.gas_limit && (
                  <span className="text-muted-foreground ml-2">
                    ({((data.gas_used / data.gas_limit) * 100).toFixed(2)}%)
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Token Transfers Section */}
      {hasTokenTransfers && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b bg-muted/30">
              <h2 className="font-semibold flex items-center gap-2">
                <Coins className="w-4 h-4" />
                Token Transfers ({data.transfers.length})
              </h2>
            </div>
            <div className="divide-y">
              {data.transfers.map((transfer, idx) => {
                const tokenAddr = transfer.token_address?.toLowerCase();
                const tokenInfo = TOKEN_INFO[tokenAddr] || { symbol: transfer.token_symbol || 'Token', name: transfer.token_name || 'Unknown', decimals: 18 };
                const fromLabel = getAddressLabel(transfer.from_address);
                const toLabel = getAddressLabel(transfer.to_address);
                const isBurn = transfer.to_address?.toLowerCase() === ZERO_ADDRESS;
                
                return (
                  <div key={`${transfer.tx_hash}-${transfer.log_index}-${idx}`} className="p-4 hover:bg-muted/30">
                    <div className="flex items-center gap-2 mb-2">
                      {isBurn ? (
                        <Badge className="bg-orange-500/20 text-orange-600">
                          <Flame className="w-3 h-3 mr-1" />
                          Burn
                        </Badge>
                      ) : (
                        <Badge variant="outline">Transfer</Badge>
                      )}
                      <Badge className="bg-primary/10 text-primary">
                        {tokenInfo.symbol}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">{truncateAddress(tokenAddr, 6, 4)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted-foreground">From</span>
                      <div className="flex items-center gap-1">
                        <AddressBadge address={transfer.from_address} truncate />
                        {fromLabel && <Badge variant="outline" className="text-xs">{fromLabel}</Badge>}
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">To</span>
                      <div className="flex items-center gap-1">
                        <AddressBadge address={transfer.to_address} truncate />
                        {toLabel && <Badge variant="outline" className="text-xs">{toLabel}</Badge>}
                      </div>
                      <span className="text-muted-foreground">For</span>
                      <Badge variant="outline" className="font-mono">
                        {formatNumber(parseFloat(transfer.amount_formatted || '0').toFixed(6))} {tokenInfo.symbol}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* DEX Swaps Section */}
      {hasSwaps && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b bg-muted/30">
              <h2 className="font-semibold flex items-center gap-2">
                <Repeat className="w-4 h-4" />
                DEX Swaps ({data.dex_swaps.length})
              </h2>
            </div>
            <div className="divide-y">
              {data.dex_swaps.map((swap, idx) => {
                const isBuy = swap.krown_direction === 'in';
                // Use user_wallet (actual transaction signer) instead of sender/recipient
                const traderAddress = swap.user_wallet || swap.sender || swap.recipient;
                const traderLabel = getAddressLabel(traderAddress);
                
                return (
                  <div key={`${swap.tx_hash}-${swap.log_index}-${idx}`} className="p-4 hover:bg-muted/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={isBuy ? 'bg-emerald-500/20 text-emerald-600' : 'bg-red-500/20 text-red-600'}>
                        {isBuy ? 'BUY' : 'SELL'} KROWN
                      </Badge>
                      <Badge variant="outline">
                        {swap.token0_symbol}/{swap.token1_symbol}
                      </Badge>
                      {swap.fee && <span className="text-xs text-muted-foreground">Fee: {(swap.fee / 10000).toFixed(2)}%</span>}
                    </div>
                    
                    {/* Swap Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground mb-1">KROWN Amount</p>
                        <p className="font-mono font-bold">{formatNumber(parseFloat(swap.krown_amount || 0).toFixed(2))}</p>
                      </div>
                      {swap.volume_usd && (
                        <div className="p-3 rounded-lg bg-muted/50">
                          <p className="text-xs text-muted-foreground mb-1">Volume (USD)</p>
                          <p className="font-mono font-bold text-emerald-600">${formatNumber(parseFloat(swap.volume_usd).toFixed(2))}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Trader Info - Now using user_wallet */}
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Trader:</span>
                      <div className="flex items-center gap-1">
                        <AddressBadge address={traderAddress} truncate />
                        {traderLabel && (
                          <Badge variant="outline" className="text-xs">{traderLabel}</Badge>
                        )}
                      </div>
                      <span className="text-muted-foreground">Pool:</span>
                      <AddressBadge address={swap.pool_address} truncate />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Input Data Section */}
      {data.input_data && data.input_data !== '0x' && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b bg-muted/30">
              <h2 className="font-semibold flex items-center gap-2">
                <Database className="w-4 h-4" />
                Input Data
              </h2>
            </div>
            <div className="p-4">
              <div className="bg-muted/30 rounded-lg p-4 overflow-x-auto">
                <code className="text-xs font-mono break-all whitespace-pre-wrap">
                  {data.input_data}
                </code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No transfers/swaps message */}
      {!hasTokenTransfers && !hasSwaps && (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              No tracked token transfers or DEX swaps in this transaction
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
